"use client";

import { CartEditTransactionsList } from "@/components/features/cart-edit/cart-edit-transactions-list";
import DailySalesDisplay from "@/components/shared/product/daily-sales-display";
import DOIDisplay from "@/components/shared/product/doi-display";
import RetailerInventoryDisplay from "@/components/shared/product/retailer-inventory";
import { ShopCartTransaction } from "@/lib/cart/cart-types";
import { ShopProductPageData } from "@/lib/product-page/product-page-types";
import { useCartStore } from "@/state-management/stores/cart-store";
import { useEffect, useState } from "react";

export default function ProductPageCartEdit({
    shopProductPageData
}: {
    shopProductPageData: ShopProductPageData;
}) {
    const { subscribeToProduct } = useCartStore();
    const [transactions, setTransactions] = useState<ShopCartTransaction[] | undefined>(undefined);

    useEffect(() => {
        const unsubscribe = subscribeToProduct(
            shopProductPageData.productID,
            (cartTransactions?: ShopCartTransaction[]) => {
                setTransactions(cartTransactions);
            }
        );

        return unsubscribe;
    }, [
        shopProductPageData.productID,
        shopProductPageData.wholesaleUnitsPerCase,
        subscribeToProduct
    ]);

    const currentCartUnits = transactions?.reduce((acc, transaction) => {
        return acc + transaction.numUnits;
    }, 0);

    return (
        <div className="space-y-1">
            <h2 className="text-lg font-semibold">Add to Cart:</h2>
            <div className="flex flex-col gap-2">
                <CartEditTransactionsList
                    transactions={transactions ?? []}
                    productID={shopProductPageData.productID}
                    productName={shopProductPageData.productName}
                    wholesaleUnitsPerCase={shopProductPageData.wholesaleUnitsPerCase}
                    sellByCaseOnly={shopProductPageData.sellByCaseOnly}
                    manualPromotionsList={
                        shopProductPageData.price?.promotions
                            ? shopProductPageData.price?.promotions
                            : null
                    }
                    thumbnailImageURL={shopProductPageData.thumbnailImageURL ?? undefined}
                    minOrderUnits={shopProductPageData.minOrderUnits}
                    baseUnitPrice={shopProductPageData.price?.unitPrice ?? 0}
                />
                <div className="mt-6 items-center rounded-lg border p-2">
                    {shopProductPageData.retailerInventory ? (
                        <div className="flex flex-row items-center justify-between gap-4 text-lg">
                            <RetailerInventoryDisplay
                                inventory={shopProductPageData.retailerInventory.inventory}
                                lastInventoryDate={
                                    shopProductPageData.retailerInventory.lastInventoryDate
                                }
                            />
                            <DOIDisplay
                                inventory={shopProductPageData.retailerInventory.inventory}
                                currentDOI={shopProductPageData.retailerInventory.DOI}
                                dailySales={shopProductPageData.retailerInventory.dailySales}
                                productID={shopProductPageData.productID}
                                currentCartUnits={currentCartUnits ?? 0}
                                showAutoButton={false}
                                wholesaleUnitsPerCase={shopProductPageData.wholesaleUnitsPerCase}
                                className="text-lg"
                            />
                            <DailySalesDisplay
                                dailySales={shopProductPageData.retailerInventory.dailySales}
                                className="text-lg"
                            />
                        </div>
                    ) : (
                        <div className="text-md m-auto w-full text-center align-middle text-gray-500">
                            No POS data for this product
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
